/**
 * API-specific transformers
 * Converts between BE types (GraphScenarioResponse) and FE types (DiagramState)
 */

import { DiagramEdge, DiagramNode } from "components/CC/Diagrams/ConnectionCreatorDiagram/ConnectionCreatorDiagram";
import { NodeType } from "components/CC/types/enums";
import { transformNodesToFlowPositionIds } from "components/CC/utils/flowPositionIds";

import {
    mapConnectionSettingsToEdgeData,
    mapEdgeDataToConnectionSettings,
    mapFormDataToStepSettings,
    mapStepSettingsToFormData,
} from "./settingsMappers";
import { mapBeTypeToFeType, mapFeTypeToBeType } from "./typeMappers";
import { DiagramState } from "../../types";

import GraphScenarioResponse = Paldock.LaravelConnectionCreator.Http.Responses.GraphScenarioResponse;
import UpdateGraphScenario = Paldock.LaravelConnectionCreator.Http.Requests.UpdateGraphScenario;
import StepData = Paldock.LaravelConnectionCreator.Data.StepData;
import ConnectionData = Paldock.LaravelConnectionCreator.Data.ConnectionData;

/**
 * Map of "prevStepUuid:nextStepUuid" -> connection UUID
 * Used to recover beId when a connection is removed and recreated between the same nodes
 */
let originalConnectionMap = new Map<string, string>();

/**
 * Transform BE GraphScenarioResponse to FE DiagramState
 */
export function transformGraphResponseToState(response: GraphScenarioResponse): DiagramState {
    if (import.meta.env.DEV) {
        console.log("[CC API] transformGraphResponseToState received:", response);
    }

    if (!response) {
        if (import.meta.env.DEV) {
            console.error("[CC API] Response is undefined!");
        }
        throw new Error("API response is undefined");
    }

    if (!response.steps) {
        if (import.meta.env.DEV) {
            console.error("[CC API] Response has no steps:", response);
        }
        throw new Error("API response has no steps array");
    }

    // 1. Filter out soft-deleted steps, then map to nodes
    const activeSteps = response.steps.filter((step: StepData) => !step.deleted_at);
    const nodes: DiagramNode[] = activeSteps.map((step: StepData) => ({
        id: step.id, // UUID - will be transformed to flow-position ID
        type: mapBeTypeToFeType(step.type),
        position: { x: step.position_x, y: step.position_y },
        data: {
            beId: step.id,
            formData: mapStepSettingsToFormData(step),
        },
    }));

    // 2. Filter connections to only include those between active steps, then map to edges
    const activeStepIds = new Set(activeSteps.map((step: StepData) => step.id));
    const activeConnections = response.connections.filter(
        (conn: ConnectionData) => activeStepIds.has(conn.prev_step_id) && activeStepIds.has(conn.next_step_id)
    );
    // Build original connection map for recovering beIds on reconnect
    originalConnectionMap = new Map();
    activeConnections.forEach((conn: ConnectionData) => {
        originalConnectionMap.set(`${conn.prev_step_id}:${conn.next_step_id}`, conn.id);
    });

    const edges: DiagramEdge[] = activeConnections.map((conn: ConnectionData) => ({
        id: conn.id,
        source: conn.prev_step_id,
        target: conn.next_step_id,
        type: "customEdge",
        data: {
            beId: conn.id,
            ...mapConnectionSettingsToEdgeData(conn),
        },
    }));

    // 3. Transform UUIDs to flow-position IDs
    const { nodes: transformedNodes, edges: transformedEdges, idMap } = transformNodesToFlowPositionIds(nodes, edges);

    // 4. Update node references in formData (e.g., mirrorElement) to use flow-position IDs
    const nodesWithUpdatedRefs = updateNodeReferencesInFormData(transformedNodes, idMap);

    if (import.meta.env.DEV) {
        console.log("[CC API] transformGraphResponseToState result:", {
            nodeCount: nodesWithUpdatedRefs.length,
            firstNode: nodesWithUpdatedRefs[0],
            hasBeId: nodesWithUpdatedRefs[0]?.data?.beId,
        });
    }

    return {
        nodes: nodesWithUpdatedRefs,
        edges: transformedEdges,
        notes: "",
    };
}

/**
 * Update node references in formData to use flow-position IDs instead of UUIDs
 */
function updateNodeReferencesInFormData(nodes: DiagramNode[], idMap: Map<string, string>): DiagramNode[] {
    // Build reverse map: UUID -> flow-position ID
    const uuidToFlowId = new Map<string, string>();
    idMap.forEach((flowId, uuid) => {
        uuidToFlowId.set(uuid, flowId);
    });

    return nodes.map((node) => {
        const formData = (node.data as Record<string, unknown>)?.formData as Record<string, unknown> | undefined;
        if (!formData) return node;

        // Handle mirror node
        if (node.type === "mirrorNode" && formData.mirrorElement) {
            const mirrorUuid = formData.mirrorElement as string;
            const mirrorFlowId = uuidToFlowId.get(mirrorUuid);
            if (mirrorFlowId) {
                return {
                    ...node,
                    data: {
                        ...(node.data as Record<string, unknown>),
                        formData: {
                            ...formData,
                            mirrorElement: mirrorFlowId,
                        },
                    },
                };
            }
        }

        return node;
    });
}

// FE-only node types that should not be sent to BE
const FE_ONLY_NODE_TYPES = ["methodsNode"];

/**
 * Transform FE DiagramState to BE UpdateGraphScenario request
 */
export function transformStateToGraphRequest(state: DiagramState, scenarioId: string): UpdateGraphScenario {
    // Filter out FE-only nodes (like methodsNode which is just UI)
    const beNodes = state.nodes.filter((node) => !FE_ONLY_NODE_TYPES.includes(node.type || ""));
    const feOnlyNodeIds = new Set(
        state.nodes.filter((node) => FE_ONLY_NODE_TYPES.includes(node.type || "")).map((node) => node.id)
    );

    // Build beId lookup map: flow-position ID -> UUID (only for existing nodes)
    const nodeBeIdMap = new Map<string, string>();
    beNodes.forEach((node) => {
        const beId = (node.data as Record<string, unknown>)?.beId as string | undefined;
        if (import.meta.env.DEV) {
            console.log("[CC API] Node", node.id, "beId:", beId, "data:", node.data);
        }
        if (beId) {
            nodeBeIdMap.set(node.id, beId);
        }
    });

    // Map nodes to steps - don't include id for new nodes, use temp_id instead
    const steps = beNodes.map((node) => {
        const formData = (node.data as Record<string, unknown>)?.formData as Record<string, unknown> | undefined;
        const beId = nodeBeIdMap.get(node.id);

        // Start nodes carry their entity-specific BE type in formData.beType;
        // all other node types use the standard FE→BE type mapping.
        const beType =
            node.type === NodeType.Start && (formData?.beType as string)
                ? (formData?.beType as string)
                : mapFeTypeToBeType(node.type || "");

        const step: Record<string, unknown> = {
            type: beType,
            title: (formData?.title as string) || "",
            subtitle: (formData?.subtitle as string) || null,
            position_x: node.position.x,
            position_y: node.position.y,
            is_enabled: (formData?.active as boolean | undefined) ?? true, // toggle hidden — defaults to enabled
            settings: mapFormDataToStepSettings(node.type || "", formData || {}, nodeBeIdMap),
        };

        if (beId) {
            // Existing node - use its BE UUID
            step.id = beId;
        } else {
            // New node - use temp_id with flow-position ID so connections can reference it
            step.temp_id = node.id;
        }

        return step;
    });

    // Filter out edges connected to FE-only nodes, then map to connections
    const beEdges = state.edges.filter((edge) => !feOnlyNodeIds.has(edge.source) && !feOnlyNodeIds.has(edge.target));

    // Map edges to connections - don't include id for new connections
    const connections = beEdges.map((edge) => {
        const edgeData = edge.data as Record<string, unknown> | undefined;
        const edgeBeId = edgeData?.beId as string | undefined;
        const settings = mapEdgeDataToConnectionSettings(edgeData);

        if (import.meta.env.DEV) {
            console.log("[CC API] Edge mapping:", {
                edgeId: edge.id,
                edgeData,
                conditions: edgeData?.conditions,
                mappedSettings: settings,
            });
        }

        const connection: Record<string, unknown> = {
            prev_step_id: nodeBeIdMap.get(edge.source) || edge.source,
            next_step_id: nodeBeIdMap.get(edge.target) || edge.target,
            title: (edgeData?.title as string) || null,
            subtitle: (edgeData?.subtitle as string) || null,
            settings,
        };

        // Include id if connection already exists in BE
        if (edgeBeId) {
            connection.id = edgeBeId;
        } else {
            // Recover beId for reconnected edges (removed and recreated between same nodes)
            const prevStepId = connection.prev_step_id as string;
            const nextStepId = connection.next_step_id as string;
            const originalBeId = originalConnectionMap.get(`${prevStepId}:${nextStepId}`);
            if (originalBeId) {
                connection.id = originalBeId;
            }
        }

        return connection;
    });

    // Cast to UpdateGraphScenario - BE types use Array<any> for settings but actual API accepts objects
    return {
        id: scenarioId,
        steps,
        connections,
    } as unknown as UpdateGraphScenario;
}

/**
 * Sync beIds from BE response after save
 * Matches nodes/edges by position (with tolerance) and type, then updates beId
 */
export function syncBeIdsFromResponse(currentState: DiagramState, response: GraphScenarioResponse): DiagramState {
    // Position tolerance for floating point comparison
    const POSITION_TOLERANCE = 0.001;

    // Update node beIds matched by position (with tolerance) and type
    const updatedNodes = currentState.nodes.map((node) => {
        const matchingStep = response.steps.find(
            (step: StepData) =>
                Math.abs(step.position_x - node.position.x) < POSITION_TOLERANCE &&
                Math.abs(step.position_y - node.position.y) < POSITION_TOLERANCE &&
                mapBeTypeToFeType(step.type) === node.type
        );
        if (matchingStep) {
            return {
                ...node,
                data: {
                    ...(node.data as Record<string, unknown>),
                    beId: matchingStep.id,
                },
            };
        }
        return node;
    });

    // Build lookup for edge matching
    const nodeBeIdMap = new Map<string, string>();
    updatedNodes.forEach((node) => {
        const beId = (node.data as Record<string, unknown>)?.beId as string;
        if (beId) nodeBeIdMap.set(node.id, beId);
    });

    // Update edge beIds matched by source/target UUIDs
    const updatedEdges = currentState.edges.map((edge) => {
        const sourceBeId = nodeBeIdMap.get(edge.source);
        const targetBeId = nodeBeIdMap.get(edge.target);
        const matchingConn = response.connections.find(
            (conn: ConnectionData) => conn.prev_step_id === sourceBeId && conn.next_step_id === targetBeId
        );
        if (matchingConn) {
            return {
                ...edge,
                data: {
                    ...(edge.data as Record<string, unknown>),
                    beId: matchingConn.id,
                },
            };
        }
        return edge;
    });

    return {
        nodes: updatedNodes,
        edges: updatedEdges,
        notes: currentState.notes,
    };
}
